import { useState, useEffect, useRef } from 'react';
import {
  Zap, Flame, Thermometer, ExternalLink, Info, ChevronRight,
  Loader2, CheckCircle2, AlertTriangle, XCircle, PencilLine,
  Database, CreditCard, Gauge, FileText, TrendingDown, Euro
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { masterSupabase } from '@/lib/supabase-master';
import { capitalFetch } from '@/services/capitalApiService.js';
/**
 * EnergyComparisonWidget — CLARA360-ENERGY-SAVINGS-1
 *
 * Modi:
 *   auto      — Vertragsdaten aus Capital Atlas (Confidence >= 0.85)
 *   candidate — Vertragsdaten vorhanden, Confidence 0.60–0.84, Bestätigung erforderlich
 *   manual    — Kein Objekt / keine Daten → manueller Eingabe-Flow
 *
 * Neu in SAVINGS-1:
 *   - Savings-Opportunity-Karte (Einsparpotenzial ~X €/Jahr)
 *   - "Tarife vergleichen" Button mit vorausgefüllter PLZ + Energieart
 *   - Nur Verbrauch muss manuell eingegeben werden, wenn nicht bekannt
 *
 * Human Override:
 *   confirm → Audit-Log: user_confirmed, weiter zum Affiliate-Flow
 *   edit    → Wechsel in manuellen Modus mit vorausgefüllten Daten
 *   reject  → Audit-Log: user_rejected, leerer manueller Modus
 */
const CAPITAL_API = '/api/capital/trpc';

// Eng begrenzter, unveränderlicher Canary-Anker. Die PLZ wird nur dann genutzt,
// wenn Contract, Primärbeleg-ID und Digest exakt zum kanonischen P7,1-Hausstrom
// passen. Sie ersetzt keine Capital-Wahrheitsschicht; sie verhindert bis zur
// Erweiterung des Hub-Read-Modells einen falschen Fallback auf die Objekt-PLZ.
const P7_OWNER_CANARY_SUPPLY_ANCHOR = Object.freeze({
  propertyId: '31c61013-8e7a-4bfd-b36a-eb94c40a579c',
  contractNumber: '24025989917',
  sourceDocumentId: '6026',
  immutableDigest: '605821d98827f0b1a20fbb2abecdda1c9226b5c355afb9a6dc394b8568a89cf6',
  postalCode: '68161',
  label: 'Lieferstelle laut primärbelegter MVV-Jahresabrechnung',
});
const SOURCE_ICONS = {
  bank_transactions: CreditCard,
  meter_points: Gauge,
  datev_entries: Database,
  vault_document: FileText,
  inferred_meter_match: Gauge,
};
const ENERGY_LABELS = {
  electricity: 'Strom',
  gas: 'Gas',
  district_heating: 'Fernwärme',
  heat: 'Wärme',
  mixed_utility: 'Sammelzahlung',
};
const ENERGY_ICONS = {
  electricity: Zap,
  gas: Flame,
  district_heating: Thermometer,
  heat: Thermometer,
};
// Energieart → Affiliate-Produkttyp Mapping
const ENERGY_TO_PRODUCT = {
  electricity: 'electricity',
  gas: 'gas',
  district_heating: 'gas', // Fernwärme → Gas-Vergleich als Fallback
  heat: 'gas',
};
const DISTRICT_HEATING_TYPES = new Set(['district_heating', 'heat']);

export default function EnergyComparisonWidget({ propertyId, organizationId }) {
  // Discovery
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryData, setDiscoveryData] = useState(null);
  // Savings
  const [savingsData, setSavingsData] = useState(null);
  const [savingsLoading, setSavingsLoading] = useState(false);
  // Property Profile (PLZ)
  const [propertyProfile, setPropertyProfile] = useState(null);
  // Widget-Modus: 'loading' | 'auto' | 'candidate' | 'manual'
  const [widgetMode, setWidgetMode] = useState('loading');
  // Override
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [overrideAction, setOverrideAction] = useState(null);
  // Affiliate-Flow
  const [step, setStep] = useState(1);
  const [productType, setProductType] = useState('electricity');
  const [zip, setZip] = useState('');
  const [zipSource, setZipSource] = useState(null);
  const [consumption, setConsumption] = useState('');
  const [householdSize, setHouseholdSize] = useState('');
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [affiliateData, setAffiliateData] = useState(null);
  const [affiliateError, setAffiliateError] = useState(null);
  const [commercialOpportunityId, setCommercialOpportunityId] = useState(null);
  // The CHECK24 script is never appended before the explicit self-service consent.
  const [externalTrackingConsent, setExternalTrackingConsent] = useState(false);
  const [connectionHolderConfirmed, setConnectionHolderConfirmed] = useState(false);
  const [embedContract, setEmbedContract] = useState(null);
  const [embedError, setEmbedError] = useState(null);
  const check24ContainerRef = useRef(null);
  /* CLARA360-EXACT-OFFER-TRANSACTION-1 — Exact-Offer-Rail (provisionsunabhängig) */
  const [canonicalRecommendation, setCanonicalRecommendation] = useState(null);
  const [exactOffer, setExactOffer] = useState(null);
  const [exactOfferLoading, setExactOfferLoading] = useState(false);
  const [exactOfferError, setExactOfferError] = useState(null);
  const [exactRailConsentFor, setExactRailConsentFor] = useState(null);
  const [exactTrackingConsent, setExactTrackingConsent] = useState(false);
  const [exactHolderConfirmed, setExactHolderConfirmed] = useState(false);
  const [exactOfferOpening, setExactOfferOpening] = useState(false);
  const [exactOfferOpened, setExactOfferOpened] = useState(null);

  // ── Discovery + Savings laden ─────────────────────────────────────
  useEffect(() => {
    if (!propertyId || propertyId === 'all' || !organizationId) {
      setWidgetMode('manual');
      setDiscoveryData(null);
      setSavingsData(null);
      setPropertyProfile(null);
      return;
    }
    loadAll(propertyId, organizationId);
  }, [propertyId, organizationId]);

  // Der Vertrags-Summary-Pfad enthält nicht für jeden materialisierten
  // Primärbeleg bereits den annualisierten Verbrauch. Der Monitoring-Hub ist
  // die kanonische, organisationsgescoppte Wahrheitsschicht dafür. Es werden
  // nur assigned_verified-Records übernommen; keine Schätzung, kein Fallback.
  const canonicalAnnualizedConsumption = (hubData, pid) => {
    const records = Array.isArray(hubData?.records) ? hubData.records : [];
    const candidate = records.find((record) => (
      record?.rowKind === 'contract'
      && record?.propertyId === pid
      && record?.contract?.providerName
      && record?.evidence?.assignmentStatus === 'assigned_verified'
      && record?.evidence?.qualityStatus === 'verified'
      && Number(record?.evidence?.annualizedConsumptionKwh || record?.recommendation?.annualConsumptionKwh) >= 100
    ));
    if (!candidate) return null;
    const recommendation = candidate.recommendation;
    const marketBest = recommendation?.baselines?.marketBest;
    const canonicalSavings = (
      recommendation?.recommendationState === 'SAVE'
      && Number(recommendation?.deltaYear1Eur) > 50
      && Number(marketBest?.year1Eur) > 0
      && Number(recommendation?.annualCostEstimateEur) > 0
    ) ? {
      found: true,
      hasSavingsPotential: true,
      estimatedSavingsYearly: Number(recommendation.deltaYear1Eur),
      savingsLabel: `Evidenzbasierte Ersparnis Jahr 1: ${Number(recommendation.deltaYear1Eur).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`,
      benchmarkMonthlyCost: Number((Number(marketBest.year1Eur) / 12).toFixed(2)),
      benchmarkSource: `Kanonischer Monitoring-Hub · Marktbestwert: ${marketBest.provider || 'nicht belegt'}${marketBest.tariff ? ` – ${marketBest.tariff}` : ''}`,
      confidence: 0.85,
      currentMonthlyCost: Number((Number(recommendation.annualCostEstimateEur) / 12).toFixed(2)),
      energyTypeLabel: candidate.contract?.title || 'Strom',
      source: 'energy_contract_hub_recommendation',
    } : null;
    const isP7OwnerCanaryEvidence = (
      candidate.propertyId === P7_OWNER_CANARY_SUPPLY_ANCHOR.propertyId
      && candidate.contract?.contractNumber === P7_OWNER_CANARY_SUPPLY_ANCHOR.contractNumber
      && String(candidate.evidence?.sourceDocumentId || '') === P7_OWNER_CANARY_SUPPLY_ANCHOR.sourceDocumentId
      && candidate.evidence?.immutableDigest === P7_OWNER_CANARY_SUPPLY_ANCHOR.immutableDigest
    );
    return {
      recommendation: candidate.recommendation || null,
      annualConsumptionKwh: Math.round(Number(candidate.evidence?.annualizedConsumptionKwh || candidate.recommendation?.annualConsumptionKwh)),
      sourceDocumentId: candidate.evidence?.sourceDocumentId || null,
      source: 'energy_contract_hub_assigned_verified',
      supplyPostalCode: isP7OwnerCanaryEvidence ? P7_OWNER_CANARY_SUPPLY_ANCHOR.postalCode : null,
      supplyPostalSource: isP7OwnerCanaryEvidence ? P7_OWNER_CANARY_SUPPLY_ANCHOR.label : null,
      savings: canonicalSavings,
    };
  };

  /* CLARA360-EXACT-OFFER-TRANSACTION-1 — Resolver-Read (read-only, organisationsgebunden).
     Die Rangfolge stammt ausschließlich vom Server; das UI sortiert nie um. */
  useEffect(() => {
    if (!propertyId || propertyId === 'all' || !organizationId || !canonicalRecommendation) {
      setExactOffer(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setExactOfferLoading(true);
      setExactOfferError(null);
      try {
        const { data: { session } } = await masterSupabase.auth.getSession();
        if (!session?.access_token) throw new Error('session_required');
        const marketBest = canonicalRecommendation?.baselines?.marketBest || {};
        const params = new URLSearchParams({
          organization_id: organizationId,
          property_id: propertyId,
          recommendation_state: String(canonicalRecommendation.recommendationState || ''),
          delta_year1_eur: String(canonicalRecommendation.deltaYear1Eur ?? ''),
          annual_cost_estimate_eur: String(canonicalRecommendation.annualCostEstimateEur ?? ''),
          market_best_provider: String(marketBest.provider || ''),
          market_best_year1_eur: String(marketBest.year1Eur ?? ''),
        });
        const res = await fetch(`/api/affiliate/exact-offer?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'x-supabase-access-token': session.access_token,
          },
        });
        if (!res.ok) throw new Error('exact_offer_unavailable');
        const data = await res.json();
        if (!cancelled) setExactOffer(data);
      } catch (_) {
        if (!cancelled) setExactOffer(null);
      } finally {
        if (!cancelled) setExactOfferLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [propertyId, organizationId, canonicalRecommendation]);
  const loadAll = async (pid, orgId) => {
    setDiscoveryLoading(true);
    setWidgetMode('loading');
    try {
      // Parallel: Summary, Objektprofil und kanonische Evidence lesen.
      const [summaryRes, profileRes, hubRes] = await Promise.all([
        capitalFetch(`${CAPITAL_API}/energyContracts.summary?input=${encodeURIComponent(JSON.stringify({ json: { propertyId: pid, organizationId: orgId } }))}`),
        capitalFetch(`${CAPITAL_API}/energyContracts.propertyProfile?input=${encodeURIComponent(JSON.stringify({ json: { propertyId: pid, organizationId: orgId } }))}`),
        capitalFetch(`${CAPITAL_API}/energyContractHub.byOrganization?input=${encodeURIComponent(JSON.stringify({ json: { organizationId: orgId } }))}`),
      ]);

      let summaryData = null;
      let profileData = null;
      let hubData = null;

      if (summaryRes.ok) {
        const raw = await summaryRes.json();
        if (!raw.error) summaryData = raw.result?.data?.json ?? null;
      }
      if (profileRes.ok) {
        const raw = await profileRes.json();
        if (!raw.error) profileData = raw.result?.data?.json ?? null;
      }
      if (hubRes.ok) {
        const raw = await hubRes.json();
        if (!raw.error) hubData = raw.result?.data?.json ?? null;
      }

      const canonical = canonicalAnnualizedConsumption(hubData, pid);
      if (canonical && summaryData?.profile) {
        // Nur die fehlende, belegte Jahresverbrauchsangabe ergänzen. Alle
        // bestehenden Vertragsfelder und Confidence-Werte bleiben unverändert.
        summaryData = {
          ...summaryData,
          profile: {
            ...summaryData.profile,
            annual_consumption_kwh: canonical.annualConsumptionKwh,
            annual_consumption_source: canonical.source,
            annual_consumption_source_document_id: canonical.sourceDocumentId,
          },
        };
      }
      if (canonical?.savings) {
        setSavingsData(canonical.savings);
      } else {
        setSavingsData(null);
      }
      setCanonicalRecommendation(canonical?.recommendation || null);

      setDiscoveryData(summaryData);
      setPropertyProfile(profileData);

      // Die Lieferstellen-PLZ aus einem verifizierten Primärbeleg schlägt
      // die Objekt-/Korrespondenz-PLZ. Ohne harte Evidence bleibt der bisherige
      // Objektprofil-Prefill erhalten.
      if (canonical?.supplyPostalCode) {
        setZip(canonical.supplyPostalCode);
        setZipSource(canonical.supplyPostalSource);
      } else if (profileData?.postalCode) {
        setZip(profileData.postalCode);
        setZipSource('Objektprofil');
      } else {
        setZipSource(null);
      }
      if (summaryData?.energyType) {
        setProductType(ENERGY_TO_PRODUCT[summaryData.energyType] || 'electricity');
      }

      if (summaryData?.widgetMode === 'auto' && summaryData?.profile?.confidence_score >= 0.85) {
        setWidgetMode('auto');
        prefillFromProfile(summaryData.profile);
        // Der kanonische Hub hat Vorrang. Der ältere propertyweite Fallback
        // darf nur für dieselbe Energieart erscheinen.
        if (!canonical?.savings) loadSavings(pid, orgId, summaryData.profile.energy_type);
      } else if (summaryData?.widgetMode === 'auto' && summaryData?.profile) {
        setWidgetMode('candidate');
        prefillFromProfile(summaryData.profile);
      } else {
        setWidgetMode('manual');
      }
    } catch (_) {
      setWidgetMode('manual');
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const loadSavings = async (pid, orgId, expectedEnergyType) => {
    setSavingsLoading(true);
    try {
      const res = await capitalFetch(
        `${CAPITAL_API}/energyContracts.savingsOpportunity?input=${encodeURIComponent(JSON.stringify({ json: { propertyId: pid, organizationId: orgId } }))}`
      );
      if (!res.ok) return;
      const raw = await res.json();
      const savings = raw.result?.data?.json;
      if (!raw.error && savings?.found && savings.energyType === expectedEnergyType) {
        setSavingsData(savings);
      } else {
        // Never show a gas benchmark in a power contract (or vice versa).
        setSavingsData(null);
      }
    } catch (_) {
      // Savings-Fehler sind nicht blockierend
    } finally {
      setSavingsLoading(false);
    }
  };

  const prefillFromProfile = (profile) => {
    if (!profile) return;
    const typeMap = { electricity: 'electricity', gas: 'gas', district_heating: 'gas', heat: 'gas' };
    setProductType(typeMap[profile.energy_type] || 'electricity');
    if (profile.annual_consumption_kwh) {
      setConsumption(String(Math.round(profile.annual_consumption_kwh)));
    }
  };

  // ── Human Override ────────────────────────────────────────────────
  const handleOverride = async (action) => {
    setOverrideLoading(true);
    setOverrideAction(action);
    try {
      const contractId = discoveryData?.profile?.contract_id;
      if (contractId) {
        await capitalFetch(`${CAPITAL_API}/energyContracts.recordOverride`, {
          method: 'POST',
          organizationId,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ json: { contractId, action, propertyId, organizationId, source: 'clara360_widget' } }),
        });
      }
    } catch (_) { /* Audit-Log-Fehler sind nicht blockierend */ }
    setOverrideLoading(false);
    setOverrideAction(null);
    if (action === 'confirmed') {
      // Direkt zum Affiliate-Flow mit vorausgefüllten Daten
      if (zip && zip.length === 5) {
        await handleWeiter();
      } else {
        setStep(1);
      }
    } else if (action === 'rejected') {
      setWidgetMode('manual');
      setConsumption('');
      setZip('');
      setSavingsData(null);
    } else if (action === 'edit') {
      setWidgetMode('manual');
    }
  };

  // ── Affiliate-Flow ────────────────────────────────────────────────
  const defaultConsumption = {
    electricity: { 1: 1500, 2: 2500, 3: 3500, 4: 4500, 5: 5500 },
    gas: { 1: 8000, 2: 12000, 3: 15000, 4: 18000, 5: 22000 },
  };
  const handleHouseholdChange = (size) => {
    setHouseholdSize(size);
    const def = defaultConsumption[productType][size];
    if (def) setConsumption(String(def));
  };
  const handleProductTypeChange = (type) => {
    setProductType(type);
    if (householdSize) {
      const def = defaultConsumption[type][householdSize];
      if (def) setConsumption(String(def));
    }
  };
  const commercialHeaders = async () => {
    const { data: { session } } = await masterSupabase.auth.getSession();
    if (!session?.access_token) throw new Error('Bitte erneut anmelden.');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      'x-supabase-access-token': session.access_token,
    };
  };

  const handleWeiter = async () => {
    if (!organizationId || !propertyId || propertyId === 'all') {
      setAffiliateError('Bitte wähle ein Objekt aus, um den Vergleich zu starten.');
      return;
    }
    if (!zip || zip.length !== 5 || !/^\d{5}$/.test(zip)) {
      setAffiliateError('Bitte eine gültige 5-stellige Postleitzahl eingeben.');
      return;
    }
    const cons = parseInt(consumption, 10);
    if (!cons || cons < 100 || cons > 100000) {
      setAffiliateError('Bitte einen realistischen Jahresverbrauch eingeben (100–100.000 kWh).');
      return;
    }
    setAffiliateError(null);
    setAffiliateLoading(true);
    try {
      const headers = await commercialHeaders();
      const eventType = discoveryData?.profile ? 'energy_savings' : 'manual_energy_check';
      const opportunityRes = await fetch('/api/affiliate/opportunities', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          organization_id: organizationId,
          property_id: propertyId,
          event_type: eventType,
          product_type: productType,
          provider_key: 'check24_direct',
          consent_status: 'essential_click_tracking',
          prefill: { postal_code: zip, annual_consumption_kwh: cons },
        }),
      });
      if (!opportunityRes.ok) throw new Error('Commercial Intent konnte nicht erstellt werden');
      const opportunity = await opportunityRes.json();
      setCommercialOpportunityId(opportunity.opportunity_id);
      // No click token and no external CHECK24 code exists at this stage. The
      // next screen exposes the disclosure and collects the explicit handoff consent.
      setAffiliateData({ ...opportunity, opportunity_id: opportunity.opportunity_id });
      setExternalTrackingConsent(false);
      setConnectionHolderConfirmed(false);
      setEmbedContract(null);
      setEmbedError(null);
      setStep(2);
    } catch (_) {
      setAffiliateError('Vergleich konnte nicht vorbereitet werden. Bitte versuche es erneut.');
    } finally {
      setAffiliateLoading(false);
    }
  };
  const handleOpenCheck24Comparison = async () => {
    if (!commercialOpportunityId || !externalTrackingConsent || !connectionHolderConfirmed) return;
    setAffiliateLoading(true);
    setAffiliateError(null);
    setEmbedError(null);
    try {
      const res = await fetch('/api/affiliate/handoff-authorize', {
        method: 'POST',
        headers: await commercialHeaders(),
        body: JSON.stringify({
          organization_id: organizationId,
          opportunity_id: commercialOpportunityId,
          consent_status: 'external_marketing_tracking',
          connection_holder_confirmed: true,
        }),
      });
      if (!res.ok) throw new Error('CHECK24-Handoff konnte nicht autorisiert werden');
      const data = await res.json();
      const handoff = data?.handoff;
      const safeScript = typeof handoff?.script_url === 'string' && handoff.script_url.startsWith('https://files.check24.net/widgets/auto/');
      if (!safeScript || handoff?.execution_mode !== 'embedded_comparison_widget') {
        throw new Error('CHECK24-Handoffvertrag ist ungültig');
      }
      setAffiliateData({ ...affiliateData, ...data });
      setEmbedContract(handoff);
      setStep(3);
    } catch (_) {
      setAffiliateError('CHECK24-Marktvergleich konnte nicht geöffnet werden. Bitte versuche es erneut.');
    } finally {
      setAffiliateLoading(false);
    }
  };
  useEffect(() => {
    if (step !== 3 || !embedContract || !check24ContainerRef.current) return undefined;
    const host = check24ContainerRef.current;
    host.replaceChildren();
    for (const [name, value] of Object.entries(embedContract.container?.attributes || {})) {
      host.setAttribute(name, String(value));
    }
    const script = document.createElement('script');
    script.src = embedContract.script_url;
    script.async = true;
    script.dataset.clara360Handoff = 'check24-direct';
    script.onload = () => setEmbedError(null);
    script.onerror = () => setEmbedError('Der CHECK24-Vergleich konnte nicht geladen werden. Bitte versuche es später erneut.');
    document.body.appendChild(script);
    return () => {
      script.remove();
      host.replaceChildren();
    };
  }, [step, embedContract]);

  // ── Hilfs-Komponenten ─────────────────────────────────────────────
  const ConfidenceBadge = ({ score, sources }) => {
    const pct = Math.round(score * 100);
    const cls = score >= 0.85
      ? 'bg-green-500/15 text-green-400 border-green-500/20'
      : score >= 0.60
        ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20'
        : 'bg-red-500/15 text-red-400 border-red-500/20';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
        <Info className="w-3 h-3" />
        {pct}%
      </span>
    );
  };

  const SourceHint = ({ hint, sources }) => {
    if (!hint) return null;
    const SourceIcon = sources?.[0] ? (SOURCE_ICONS[sources[0]] || Database) : Database;
    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-white/5 rounded-lg px-3 py-1.5">
        <SourceIcon className="w-3 h-3 flex-shrink-0" />
        <span>{hint}</span>
      </div>
    );
  };

  const ContractGrid = ({ profile }) => (
    <div className="grid grid-cols-2 gap-2">
      <div className="p-2.5 rounded-lg bg-white/5 border border-white/10">
        <p className="text-xs text-gray-500 mb-0.5">Energieart</p>
        <p className="text-sm text-white font-medium">{ENERGY_LABELS[profile.energy_type] || profile.energy_type}</p>
      </div>
      <div className="p-2.5 rounded-lg bg-white/5 border border-white/10">
        <p className="text-xs text-gray-500 mb-0.5">Anbieter</p>
        <p className="text-sm text-white font-medium truncate">{profile.provider || '—'}</p>
      </div>
      {profile.monthly_cost && (
        <div className="p-2.5 rounded-lg bg-white/5 border border-white/10">
          <p className="text-xs text-gray-500 mb-0.5">Monatlicher Abschlag</p>
          <p className="text-sm text-white font-medium">
            {Number(profile.monthly_cost).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
          </p>
        </div>
      )}
      {profile.annual_consumption_kwh && (
        <div className="p-2.5 rounded-lg bg-white/5 border border-white/10">
          <p className="text-xs text-gray-500 mb-0.5">Jahresverbrauch</p>
          <p className="text-sm text-white font-medium">
            {Number(profile.annual_consumption_kwh).toLocaleString('de-DE')} kWh
          </p>
        </div>
      )}
    </div>
  );

  // ── Savings-Opportunity-Karte ─────────────────────────────────────
  const SavingsCard = ({ savings }) => {
    if (!savings?.found) return null;
    const isHighSavings = savings.estimatedSavingsYearly > 500;
    const isMediumSavings = savings.estimatedSavingsYearly > 50;
    if (!isMediumSavings) return null;
    const formatMonthlyEur = (value) => Number(value || 0).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return (
      <div className={`rounded-lg border p-3 ${
        isHighSavings
          ? 'border-green-500/30 bg-green-500/8'
          : 'border-yellow-500/20 bg-yellow-500/5'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
            isHighSavings ? 'bg-green-500/20' : 'bg-yellow-500/15'
          }`}>
            <TrendingDown className={`w-3.5 h-3.5 ${isHighSavings ? 'text-green-400' : 'text-yellow-400'}`} />
          </div>
          <div>
            <p className={`text-xs font-semibold ${isHighSavings ? 'text-green-300' : 'text-yellow-300'}`}>
              {savings.savingsLabel}
            </p>
            <p className="text-xs text-gray-500">
              Benchmark: {formatMonthlyEur(savings.benchmarkMonthlyCost)} €/Monat ({savings.energyTypeLabel})
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500 bg-white/5 rounded px-2 py-1">
          <span>Aktuell: {formatMonthlyEur(savings.currentMonthlyCost)} €/Monat</span>
          <span>Markt: {formatMonthlyEur(savings.benchmarkMonthlyCost)} €/Monat</span>
        </div>
        <p className="text-xs text-gray-600 mt-1.5">
          Quelle: {savings.benchmarkSource} · Confidence {Math.round(savings.confidence * 100)}%
        </p>
      </div>
    );
  };

  /* CLARA360-EXACT-OFFER-TRANSACTION-1 — Exact-Offer-Panel
   *
   * Zeigt die provisionsunabhängige Rangfolge der recherchierten Angebote
   * (Rangfolge ausschließlich aus der Wahrheitsschicht) und die gekenn-
   * zeichneten, attribuierten Abschlusswege. Ein Rail zu einem Angebot
   * AUSSERHALB der Empfehlung trägt IMMER die Substitutionskennzeichnung.
   * Es wird kein Vertragsschluss behauptet und keine Preiszusage gegeben. */
  const formatExactEur = (value) => Number(value || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const handleExactOfferOpen = async (rail) => {
    if (!rail?.transaction_rail || !exactTrackingConsent || !exactHolderConfirmed) return;
    setExactOfferOpening(true);
    setExactOfferError(null);
    try {
      const headers = await commercialHeaders();
      const opportunityRes = await fetch('/api/affiliate/opportunities', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          organization_id: organizationId,
          property_id: propertyId,
          event_type: 'energy_savings',
          product_type: 'electricity',
          provider_key: rail.transaction_rail,
          consent_status: 'essential_click_tracking',
          prefill: { postal_code: zip, annual_consumption_kwh: parseInt(consumption, 10) },
        }),
      });
      if (!opportunityRes.ok) throw new Error('opportunity_failed');
      const opportunity = await opportunityRes.json();
      const linkRes = await fetch('/api/affiliate/link', {
        method: 'POST',
        headers,
        body: JSON.stringify({ organization_id: organizationId, opportunity_id: opportunity.opportunity_id }),
      });
      if (!linkRes.ok) throw new Error('link_failed');
      const redirectRes = await fetch('/api/affiliate/redirect-intent', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          organization_id: organizationId,
          opportunity_id: opportunity.opportunity_id,
          consent_status: 'external_marketing_tracking',
        }),
      });
      if (!redirectRes.ok) throw new Error('redirect_failed');
      const redirectData = await redirectRes.json();
      const destinationUrl = redirectData?.destination_url;
      if (typeof destinationUrl !== 'string' || !destinationUrl.startsWith('https://www.awin1.com/')) {
        throw new Error('destination_not_allowlisted');
      }
      window.open(destinationUrl, '_blank', 'noopener,noreferrer');
      setExactOfferOpened({
        tariff: rail.tariff_name,
        provider: rail.provider_name,
      });
      setExactRailConsentFor(null);
      setExactTrackingConsent(false);
      setExactHolderConfirmed(false);
    } catch (_) {
      setExactOfferError('Der Abschlussweg konnte nicht geöffnet werden. Bitte versuche es später erneut.');
    } finally {
      setExactOfferOpening(false);
    }
  };
  const ExactOfferPanel = () => {
    if (exactOfferLoading && !exactOffer) {
      return (
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          Recherchierte Angebote werden geladen…
        </div>
      );
    }
    if (!exactOffer?.recommended_offer) return null;
    const rec = exactOffer.recommended_offer;
    const ranking = Array.isArray(exactOffer.full_ranking) ? exactOffer.full_ranking : [];
    const rails = Array.isArray(exactOffer.exact_offer_rails) ? exactOffer.exact_offer_rails : [];
    const consentRail = rails.find((r) => r.offer_key === exactRailConsentFor) || null;
    return (
      <div className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2.5">
        <div className="flex items-center gap-1.5">
          <TrendingDown className="w-4 h-4 text-green-400 flex-shrink-0" />
          <p className="text-xs font-semibold text-white">Recherchierte Angebote — Rangfolge provisionsunabhängig</p>
        </div>
        {/* Kanonische Rangfolge (serverseitig, nie neu sortiert) */}
        <div className="space-y-1.5">
          {ranking.map((r) => (
            <div
              key={r.offer_key}
              className={`rounded-lg border px-2.5 py-2 ${
                r.rank === 1
                  ? 'border-green-500/30 bg-green-500/8'
                  : 'border-white/10 bg-white/5'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-white font-medium truncate">
                    <span className="text-gray-500 mr-1">#{r.rank}</span>
                    {r.provider_name} – {r.tariff_name}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    Jahr 1: {formatExactEur(r.year1_eur)} € · Jahr 2: {formatExactEur(r.year2_eur)} €
                  </p>
                </div>
                {r.rank === 1 && (
                  <span className="text-[10px] font-semibold text-green-300 border border-green-500/30 rounded-full px-2 py-0.5 whitespace-nowrap">
                    Claras Empfehlung
                  </span>
                )}
              </div>
              <p className="text-[10px] text-gray-600 mt-0.5">
                Recherchiert, nicht live bestätigt · Quelle: {r.evidence_source}
              </p>
            </div>
          ))}
        </div>
        {/* Transparenz: Empfehlung ohne eigenen Rail */}
        {exactOffer.disclosure_required && exactOffer.disclosure_text && (
          <div className="flex items-start gap-1.5 text-[11px] text-amber-200/80 bg-amber-500/8 border border-amber-500/20 rounded-lg px-2.5 py-2">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>{exactOffer.disclosure_text}</span>
          </div>
        )}
        {/* Gekennzeichnete, attribuierte Abschlusswege */}
        {rails.map((rail) => (
          <div key={rail.offer_key} className="rounded-lg border border-white/10 bg-white/5 p-2.5 space-y-1.5">
            <p className="text-xs text-white font-medium">
              Angebot ansehen: {rail.provider_name} – {rail.tariff_name}
            </p>
            {rail.substitution_note && (
              <p className="text-[11px] text-yellow-300/80">{rail.substitution_note}</p>
            )}
            <p className="text-[10px] text-gray-500">{rail.price_disclaimer}</p>
            {consentRail?.offer_key === rail.offer_key ? (
              <div className="space-y-2 pt-1">
                <label className="flex items-start gap-2 text-[11px] text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={exactTrackingConsent}
                    onChange={(e) => setExactTrackingConsent(e.target.checked)}
                    className="mt-0.5 accent-yellow-400"
                  />
                  <span>Ich willige ein, dass für diesen Weg PLZ, Jahresverbrauch und eine pseudonyme Tracking-ID an den Partner (Awin/MVV) übermittelt werden.</span>
                </label>
                <label className="flex items-start gap-2 text-[11px] text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={exactHolderConfirmed}
                    onChange={(e) => setExactHolderConfirmed(e.target.checked)}
                    className="mt-0.5 accent-yellow-400"
                  />
                  <span>Ich bestätige, dass ich als Anschluss- bzw. Vertragsinhaber handle, den Weg freiwillig öffne und einen Abschluss ausschließlich selbst auf der Anbieterseite prüfe und vornehme.</span>
                </label>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleExactOfferOpen(rail)}
                    disabled={exactOfferOpening || !exactTrackingConsent || !exactHolderConfirmed}
                    className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold text-xs h-8 disabled:opacity-50"
                  >
                    {exactOfferOpening
                      ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Wird geöffnet…</>
                      : <><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Angebot ansehen (öffnet Anbieterseite)</>}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setExactRailConsentFor(null); setExactTrackingConsent(false); setExactHolderConfirmed(false); }}
                    className="border-white/10 text-gray-400 hover:text-white text-xs h-8 px-3"
                  >
                    Zurück
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                onClick={() => { setExactRailConsentFor(rail.offer_key); setExactTrackingConsent(false); setExactHolderConfirmed(false); }}
                className="w-full bg-white/10 hover:bg-white/20 text-white font-medium text-xs h-8"
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Angebot ansehen
              </Button>
            )}
          </div>
        ))}
        {/* Bestätigung nach geöffnetem Weg — kein Abschlussbehauptung */}
        {exactOfferOpened && (
          <div className="flex items-start gap-1.5 text-[11px] text-green-300/90 bg-green-500/8 border border-green-500/20 rounded-lg px-2.5 py-2">
            <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              Der Abschlussweg „{exactOfferOpened.provider} – {exactOfferOpened.tariff}“ wurde in einem neuen Tab geöffnet.
              Der dort berechnete Live-Preis ist verbindlich. Clara360 hat keinen Vertrag abgeschlossen.
            </span>
          </div>
        )}
        {exactOfferError && (
          <p className="text-[11px] text-red-400 bg-red-500/10 rounded-lg px-2.5 py-2">{exactOfferError}</p>
        )}
        {/* Immer sichtbare Preis- und Vertragsgrenze */}
        <div className="flex items-start gap-1.5 text-[10px] text-gray-500 bg-white/5 rounded-lg px-2.5 py-1.5">
          <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span>
            {exactOffer.price_disclaimer} {exactOffer.contract_claims}
            {Array.isArray(exactOffer.rails_not_available) && exactOffer.rails_not_available.length > 0 && (
              <> Für {exactOffer.rails_not_available.map((r) => r.label || r.offer_key).join(' und ')} ist aktuell kein direkter Abschlussweg hinterlegt.</>
            )}
          </span>
        </div>
      </div>
    );
  };
  const DistrictHeatingAdvisory = () => (
    <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 space-y-1.5">
      <div className="flex items-center gap-1.5 text-sm font-medium text-blue-200">
        <Info className="w-4 h-4 flex-shrink-0" />
        <span>Fernwärme: kein automatischer Tarifvergleich</span>
      </div>
      <p className="text-xs leading-relaxed text-blue-100/70">
        Fernwärme wird lokal bzw. durch den Versorger geregelt. Bitte prüfe Vertragskonditionen und Preisentwicklung direkt mit dem Versorger.
      </p>
    </div>
  );

  const OverrideButtons = ({ confirmLabel = 'Tarife vergleichen', showConsumptionFirst = false }) => (
    <div className="space-y-2">
      {/* Verbrauch-Eingabe wenn PLZ bekannt aber Verbrauch unbekannt */}
      {showConsumptionFirst && !consumption && (
        <div>
          <Label htmlFor="ecw-consumption-auto" className="text-xs text-gray-400 mb-1 block">
            Jahresverbrauch (kWh) — für Tarifvergleich
          </Label>
          <div className="relative">
            <Input
              id="ecw-consumption-auto"
              type="number"
              min={100}
              max={100000}
              placeholder={productType === 'electricity' ? '3.500' : '15.000'}
              value={consumption}
              onChange={(e) => setConsumption(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 text-sm h-9 pr-12"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">kWh</span>
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <Button
          onClick={() => handleOverride('confirmed')}
          disabled={overrideLoading || (showConsumptionFirst && !consumption)}
          className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold text-sm h-9"
        >
          {overrideLoading && overrideAction === 'confirmed'
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <><CheckCircle2 className="w-4 h-4 mr-1.5" />{confirmLabel}</>}
        </Button>
        <Button
          variant="outline"
          onClick={() => handleOverride('edit')}
          disabled={overrideLoading}
          className="border-white/10 text-gray-400 hover:text-white text-sm h-9 px-3"
          title="Daten korrigieren"
        >
          <PencilLine className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          onClick={() => handleOverride('rejected')}
          disabled={overrideLoading}
          className="border-red-500/20 text-red-400 hover:text-red-300 hover:border-red-500/40 text-sm h-9 px-3"
          title="Daten ablehnen"
        >
          <XCircle className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // ── Render: Loading ───────────────────────────────────────────────
  if (widgetMode === 'loading' || discoveryLoading) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/3 p-4 mb-6">
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          Vertragsdaten werden geladen…
        </div>
      </div>
    );
  }

  // ── Render: Auto-Modus ────────────────────────────────────────────
  if ((widgetMode === 'auto' || widgetMode === 'candidate') && discoveryData?.profile && step === 1) {
    const p = discoveryData.profile;
    const isAuto = widgetMode === 'auto';
    const EnergyIcon = ENERGY_ICONS[p.energy_type] || Zap;
    const hasZip = zip && zip.length === 5;
    const hasConsumption = consumption && parseInt(consumption) > 100;

    return (
      <div className={`rounded-xl border p-4 mb-6 space-y-3 ${
        isAuto
          ? 'border-white/10 bg-white/3'
          : 'border-yellow-500/20 bg-yellow-500/5'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isAuto
              ? <EnergyIcon className="w-4 h-4 text-yellow-400" />
              : <AlertTriangle className="w-4 h-4 text-yellow-400" />}
            <span className="text-sm font-semibold text-white">
              {isAuto ? 'Energievertrag erkannt' : 'Vertrag erkannt — bitte prüfen'}
            </span>
          </div>
          <ConfidenceBadge score={p.confidence_score} sources={discoveryData.sources} />
        </div>

        {/* Vertragsdaten */}
        <ContractGrid profile={p} />

        {/* PLZ-Info wenn verfügbar */}
        {hasZip && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-white/5 rounded-lg px-3 py-1.5">
            <Info className="w-3 h-3 flex-shrink-0" />
            <span>PLZ {zip} ({zipSource || propertyProfile?.city || 'erkannt'}) — für Tarifvergleich vorausgefüllt{zipSource === 'Objektprofil' ? ' · Objekt-/Korrespondenz-PLZ, keine evidenzbelegte Lieferstellen-PLZ — bitte vor dem Vergleich prüfen' : ''}</span>
          </div>
        )}

        {/* Datenherkunft */}
        <SourceHint hint={discoveryData.widgetHint} sources={discoveryData.sources} />

        {/* Savings-Opportunity-Karte */}
        {savingsLoading && (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="w-3 h-3 animate-spin" />
            Einsparpotenzial wird berechnet…
          </div>
        )}
        {!savingsLoading && savingsData && (
          <SavingsCard savings={savingsData} energyType={p.energy_type} />
        )}
        {/* CLARA360-EXACT-OFFER-TRANSACTION-1 — Exact-Offer-Panel */}
        {!savingsLoading && <ExactOfferPanel />}

        {/* Hinweis bei Kandidaten */}
        {!isAuto && (
          <p className="text-xs text-yellow-300/70">
            Diese Daten wurden automatisch erkannt, sind aber noch nicht bestätigt.
            Bitte prüfe die Angaben und bestätige oder korrigiere sie.
          </p>
        )}

        {/* Fernwärme: Advisory statt Check24-Button */}
        {DISTRICT_HEATING_TYPES.has(p.energy_type) ? (
          <DistrictHeatingAdvisory profile={p} />
        ) : (
          <OverrideButtons
            confirmLabel={isAuto ? 'Tarife vergleichen' : 'Bestätigen & vergleichen'}
            showConsumptionFirst={hasZip && !hasConsumption}
          />
        )}
      </div>
    );
  }

  // ── Render: explizite CHECK24-Self-Service-Freigabe ────────────────
  if (step === 2 && affiliateData) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/3 p-4 mb-6 space-y-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-400" />
          <div>
            <h3 className="text-sm font-semibold text-white">CHECK24-Marktvergleich vorbereiten</h3>
            <p className="text-xs text-gray-400">
              Strom · PLZ {zip} · {parseInt(consumption).toLocaleString('de-DE')} kWh/Jahr
            </p>
          </div>
        </div>
        {savingsData?.found && savingsData.hasSavingsPotential && (
          <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2">
            <TrendingDown className="w-4 h-4 text-green-400 flex-shrink-0" />
            <p className="text-xs text-green-300">{savingsData.savingsLabel}</p>
          </div>
        )}
        <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-xs leading-relaxed text-blue-100/80 space-y-2">
          <p className="font-medium text-blue-200">Der Vergleich findet bei CHECK24 statt</p>
          <p>Übermittelt werden erst nach deiner Bestätigung ausschließlich PLZ, Jahresverbrauch und eine pseudonyme Tracking-ID. CHECK24 zeigt die Live-Angebote; Clara360 liest oder speichert diese Angebote nicht.</p>
          <p>Bitte prüfe die Angaben bei CHECK24 und führe eine Anfrage oder einen Abschluss ausschließlich selbst durch.</p>
        </div>
        {affiliateData.disclosure?.text && (
          <p className="text-xs text-gray-500">{affiliateData.disclosure.text}</p>
        )}
        <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={externalTrackingConsent}
            onChange={(e) => setExternalTrackingConsent(e.target.checked)}
            className="mt-0.5 accent-yellow-400"
          />
          <span>Ich willige ein, dass CHECK24 die genannten Daten und die pseudonyme Tracking-ID für diesen Marktvergleich verarbeitet.</span>
        </label>
        <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={connectionHolderConfirmed}
            onChange={(e) => setConnectionHolderConfirmed(e.target.checked)}
            className="mt-0.5 accent-yellow-400"
          />
          <span>Ich bestätige, dass ich für diesen Marktvergleich als Anschluss- bzw. Vertragsinhaber handle und den Vergleich selbst prüfe und abschließe.</span>
        </label>
        {affiliateError && (
          <p className="text-xs text-red-400 bg-red-500/10 rounded-lg px-3 py-2">{affiliateError}</p>
        )}
        <Button
          onClick={handleOpenCheck24Comparison}
          disabled={affiliateLoading || !externalTrackingConsent || !connectionHolderConfirmed}
          className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-semibold text-sm h-10 disabled:opacity-50"
        >
          {affiliateLoading
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird geöffnet…</>
            : <><ExternalLink className="w-4 h-4 mr-2" /> CHECK24-Marktvergleich öffnen</>}
        </Button>
        <button
          onClick={() => { setStep(1); setAffiliateData(null); setCommercialOpportunityId(null); setEmbedContract(null); setAffiliateError(null); }}
          className="w-full text-xs text-gray-500 hover:text-gray-400 py-1"
        >
          ← Zurück
        </button>
      </div>
    );
  }
  // ── Render: CHECK24-Widget erst nach expliziter Nutzerfreigabe ─────────
  if (step === 3 && embedContract) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/3 p-4 mb-6 space-y-4">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-400" />
          <div>
            <h3 className="text-sm font-semibold text-white">CHECK24-Marktvergleich</h3>
            <p className="text-xs text-gray-400">Vorausgefüllt mit PLZ {zip} und {parseInt(consumption).toLocaleString('de-DE')} kWh/Jahr</p>
          </div>
        </div>
        <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 text-xs leading-relaxed text-blue-100/80">
          Bitte prüfe die Angaben im CHECK24-Formular. Die dort angezeigten Preise und Bedingungen sind die aktuelle Angebotswahrheit; Clara360 gibt keine Preiszusage und schließt keinen Vertrag für dich ab.
        </div>
        <div ref={check24ContainerRef} id={embedContract.container?.id || 'c24pp-power-iframe'} className="min-h-[280px] rounded-lg overflow-hidden bg-white" />
        {embedError && <p className="text-xs text-red-400 bg-red-500/10 rounded-lg px-3 py-2">{embedError}</p>}
        <button
          onClick={() => { setStep(2); setEmbedContract(null); }}
          className="w-full text-xs text-gray-500 hover:text-gray-400 py-1"
        >
          ← Zurück zur Einwilligung
        </button>
      </div>
    );
  }

  // ── Render: Manueller Eingabe-Flow ────────────────────────────────
  return (
    <div className="rounded-xl border border-white/10 bg-white/3 p-4 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-yellow-500/20 flex items-center justify-center">
          <Zap className="w-4 h-4 text-yellow-400" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-white">Energietarif vergleichen</h3>
          <p className="text-xs text-gray-400">Günstigere Tarife in Ihrer Region finden</p>
        </div>
        <div className="ml-auto flex items-center gap-1 text-xs text-gray-500">
          <span className={step === 1 ? 'text-yellow-400 font-medium' : ''}>Eingabe</span>
          <ChevronRight className="w-3 h-3" />
          <span className={step === 2 ? 'text-yellow-400 font-medium' : ''}>Vergleich</span>
        </div>
      </div>
      {(!propertyId || propertyId === 'all') && (
        <p className="text-xs text-gray-500 bg-white/5 rounded-lg px-3 py-2 mb-4">
          Wähle oben ein Objekt aus, um erkannte Vertragsdaten zu laden.
        </p>
      )}
      <div className="space-y-4">
        {/* Energieart */}
        <div>
          <Label className="text-xs text-gray-400 mb-2 block">Energieart</Label>
          <div className="flex gap-2">
            {[
              { type: 'electricity', label: 'Strom', Icon: Zap, active: 'border-yellow-500 bg-yellow-500/15 text-yellow-300' },
              { type: 'gas', label: 'Gas', Icon: Flame, active: 'border-blue-500 bg-blue-500/15 text-blue-300' },
            ].map(({ type, label, Icon, active }) => (
              <button
                key={type}
                onClick={() => handleProductTypeChange(type)}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                  productType === type ? active : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/20'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
        {/* PLZ */}
        <div>
          <Label htmlFor="ecw-zip" className="text-xs text-gray-400 mb-1 block">
            Postleitzahl
            {zip && <span className="ml-1 text-green-400">(vorausgefüllt)</span>}
          </Label>
          <Input
            id="ecw-zip"
            type="text"
            inputMode="numeric"
            maxLength={5}
            placeholder="z. B. 68159"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
            className={`bg-white/5 border-white/10 text-white placeholder:text-gray-600 text-sm h-9 ${zip ? 'border-green-500/30' : ''}`}
          />
        </div>
        {/* Haushaltsgröße */}
        <div>
          <Label className="text-xs text-gray-400 mb-2 block">
            Haushaltsgröße <span className="text-gray-600">(optional)</span>
          </Label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => handleHouseholdChange(n)}
                className={`flex-1 py-1.5 rounded-md text-xs font-medium border transition-all ${
                  householdSize === n
                    ? 'border-purple-500 bg-purple-500/15 text-purple-300'
                    : 'border-white/10 bg-white/5 text-gray-500 hover:border-white/20'
                }`}
              >
                {n === 5 ? '5+' : n}
              </button>
            ))}
          </div>
        </div>
        {/* Jahresverbrauch */}
        <div>
          <Label htmlFor="ecw-consumption" className="text-xs text-gray-400 mb-1 block">
            Jahresverbrauch (kWh)
          </Label>
          <div className="relative">
            <Input
              id="ecw-consumption"
              type="number"
              min={100}
              max={100000}
              placeholder={productType === 'electricity' ? '3.500' : '15.000'}
              value={consumption}
              onChange={(e) => setConsumption(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 text-sm h-9 pr-12"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">kWh</span>
          </div>
          <p className="text-xs text-gray-600 mt-1">
            Ø Haushalt: {productType === 'electricity' ? '3.500 kWh Strom' : '15.000 kWh Gas'} pro Jahr
          </p>
        </div>
        {affiliateError && (
          <p className="text-xs text-red-400 bg-red-500/10 rounded-lg px-3 py-2">{affiliateError}</p>
        )}
        <Button
          onClick={handleWeiter}
          disabled={affiliateLoading || !zip || !consumption}
          className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-semibold text-sm h-9"
        >
          {affiliateLoading
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird geladen…</>
            : <>Tarife vergleichen <ChevronRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </div>
    </div>
  );
}
